// @ts-nocheck
import * as React from "react";
import { createPortal } from "react-dom";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { getGoogle, geocodeAddress } from "../lib/maps/placesClient";
import { searchPlacesServer, getPlaceDetailsServer, type ServerPlace } from "../services/placesSearch";

type LatLng = { lat: number; lng: number };

type Draft = Partial<{
  restaurant_name: string;
  restaurant_address: string;
  restaurant_location: string;
  restaurant_cuisine: string;
  restaurant_rating: number;
  opening_hours_weekday_text: string[] | undefined;
  enrich_place_id: string | null;
  enrich_place_name: string | null;
  enrich_place_address: string | null;
  enrich_confidence: number | null;
  enrich_suggestions: Array<{
    place_id?: string;
    name?: string;
    address?: string;
    enrich_confidence?: number;
  }>;
  enrich_place_lat: number | null;
  enrich_place_lng: number | null;
  google_maps_url?: string;
}>;

interface Props {
  open: boolean;
  draft: Draft | null;
  onCancel: () => void;
  onConfirm: (fields: Draft) => void;
  confirmLabel?: string; // i18n: default "Save"
  useSuggestionLabel?: string; // i18n: default "Use"
}

function clamp01(x: unknown) {
  const n = typeof x === "number" ? x : Number(x);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;
}

function useScrollLock(on: boolean) {
  React.useEffect(() => {
    if (!on) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [on]);
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  const selectors = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");
  return Array.from(container.querySelectorAll<HTMLElement>(selectors)).filter(
    (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1 && !el.getAttribute("aria-hidden")
  );
}

export default function ConfirmExtractDialog({
  open,
  draft,
  onCancel,
  onConfirm,
  confirmLabel = "Save",
  useSuggestionLabel = "Usar",
}: Props) {
  const [fields, setFields] = React.useState<Draft>(() => draft || {});
  const [ready, setReady] = React.useState(false);
  const [pos, setPos] = React.useState<LatLng | null>(null);
  const [autocomplete, setAutocomplete] = React.useState<any>(null);
  const [suggestions, setSuggestions] = React.useState<any[]>([]);
  const [liveResults, setLiveResults] = React.useState<ServerPlace[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  useScrollLock(open);

  // Prefill fields from draft once per open
  React.useEffect(() => {
    if (!open) return;
    const base = draft || {};
    const merged: Draft = { ...base };
    if (base.enrich_place_name && (!base.restaurant_name || base.restaurant_name === "N/A")) {
      merged.restaurant_name = base.enrich_place_name;
    }
    if (base.enrich_place_address && (!base.restaurant_address || base.restaurant_address === "N/A")) {
      merged.restaurant_address = base.enrich_place_address;
    }
    setFields(merged);
  }, [open, draft]);

  // Load Google + AutocompleteService safely
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getGoogle()
      .then((g) => {
        if (cancelled) return;
        setReady(true);
        try {
          const svc = new g.maps.places.AutocompleteService();
          setAutocomplete(svc);
        } catch {
          // ignore
        }
      })
      .catch(() => setReady(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Geocode + Autocomplete predictions (debounced & race-safe)
  React.useEffect(() => {
    if (!open) return;
    const q = [fields.restaurant_address, fields.restaurant_name, fields.restaurant_location]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (!q) {
      setSuggestions([]);
      return;
    }

    let alive = true;
    // Geocode
    geocodeAddress(q)
      .then((p) => alive && p && setPos(p))
      .catch(() => {});

    // Predictions
    if (autocomplete) {
      try {
        autocomplete.getPlacePredictions({ input: q }, (preds: any) => {
          if (!alive) return;
          setSuggestions((preds || []).slice(0, 5));
        });
      } catch {
        // ignore
      }
    }

    return () => {
      alive = false;
    };
  }, [open, autocomplete, fields.restaurant_address, fields.restaurant_name, fields.restaurant_location]);

  // Server-side Places text search (debounced)
  React.useEffect(() => {
    if (!open) return;
    const q = [fields.restaurant_name, fields.restaurant_address].filter(Boolean).join(" ").trim();
    if (!q) {
      setLiveResults([]);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchPlacesServer(q, { signal: ctrl.signal });
        if (!ctrl.signal.aborted) setLiveResults((res || []).slice(0, 6));
      } catch {
        if (!ctrl.signal.aborted) setLiveResults([]);
      } finally {
        if (!ctrl.signal.aborted) setIsSearching(false);
      }
    }, 350);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [open, fields.restaurant_name, fields.restaurant_address]);

  // Focus management: focus panel on open, trap Tab, handle Escape
  React.useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = getFocusable(panel);
    (focusables[0] || panel).focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      } else if (e.key === "Tab") {
        const els = getFocusable(panel);
        if (els.length === 0) return;
        const first = els[0];
        const last = els[els.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !panel.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !panel.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const useSuggested = Boolean(draft?.enrich_place_name || draft?.enrich_place_address);
  const suggestionsList = Array.isArray(draft?.enrich_suggestions) ? draft!.enrich_suggestions! : [];

  const body = (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel(); // backdrop only
      }}
    >
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-extract-title"
        aria-describedby="confirm-extract-desc"
        className="relative bottom-0 left-0 right-0 max-h-[80vh] w-full max-w-xl overflow-auto rounded-t-2xl border border-gray-200 bg-white p-4 shadow-lg outline-none dark:border-gray-800 dark:bg-gray-900"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <h2 id="confirm-extract-title" className="text-lg font-semibold">
            Confirm details
          </h2>
          <button
            type="button"
            className="rounded px-2 py-1 text-sm hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:hover:bg-gray-800"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>

        <p id="confirm-extract-desc" className="mb-2 text-sm text-gray-600">
          We found potential matches. Pick or edit the correct venue:
        </p>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* Left: form + suggestions */}
          <div className="space-y-2">
            <label className="block text-sm">
              Restaurant name
              <input
                className="mt-1 w-full rounded border border-gray-300 bg-transparent p-2 focus:outline-none focus:ring dark:border-gray-700"
                value={fields.restaurant_name || ""}
                onChange={(e) => setFields((f) => ({ ...f, restaurant_name: e.target.value }))}
              />
            </label>

            <label className="block text-sm">
              Address
              <input
                className="mt-1 w-full rounded border border-gray-300 bg-transparent p-2 focus:outline-none focus:ring dark:border-gray-700"
                value={fields.restaurant_address || ""}
                onChange={(e) => setFields((f) => ({ ...f, restaurant_address: e.target.value }))}
              />
            </label>

            <label className="block text-sm">
              Cuisine
              <input
                className="mt-1 w-full rounded border border-gray-300 bg-transparent p-2 focus:outline-none focus:ring dark:border-gray-700"
                value={fields.restaurant_cuisine || ""}
                onChange={(e) => setFields((f) => ({ ...f, restaurant_cuisine: e.target.value }))}
              />
            </label>

            {(draft?.enrich_place_name || typeof draft?.enrich_confidence === "number") && (
              <div className="text-xs text-gray-600">
                Google match:{" "}
                <span className="font-medium">{draft?.enrich_place_name || "—"}</span>{" "}
                {typeof draft?.enrich_confidence === "number" && (
                  <span>
                    (confidence {Math.round(clamp01(draft!.enrich_confidence) * 100)}%)
                  </span>
                )}
              </div>
            )}

            {/* Inline suggestion chips from enrichment */}
            {suggestionsList.length > 0 && (
              <div className="mt-2 rounded border border-gray-200 dark:border-gray-800">
                {suggestionsList.map((s, i) => {
                  const pct = Math.round(clamp01(s.enrich_confidence) * 100);
                  return (
                    <div key={s.place_id || i} className="flex items-start justify-between gap-2 px-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{s.name || "—"}</div>
                        <div className="truncate text-xs text-gray-500">{s.address || "—"}</div>
                        {Number.isFinite(pct) && (
                          <div
                            role="meter"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={pct}
                            aria-valuetext={`${pct}%`}
                            className="mt-1 h-1.5 w-24 overflow-hidden rounded bg-gray-100"
                          >
                            <div className="h-1.5 bg-blue-500" style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </div>
                      <button
                        className="shrink-0 rounded bg-blue-600 px-2 py-1 text-sm text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                        onClick={async () => {
                          const pid = s.place_id || draft?.enrich_place_id || "";
                          let details: any = null;
                          if (pid) {
                            try {
                              details = await getPlaceDetailsServer(pid);
                            } catch {}
                          }
                          const mapsUrl = pid
                            ? `https://www.google.com/maps/place/?q=place_id:${pid}`
                            : undefined;
                          const next: Draft = {
                            ...fields,
                            restaurant_name: s.name || details?.name || fields.restaurant_name,
                            restaurant_address:
                              s.address || details?.formatted_address || fields.restaurant_address,
                            restaurant_rating: (details?.rating ?? fields.restaurant_rating) as any,
                            opening_hours_weekday_text:
                              (Array.isArray(details?.opening_hours?.weekday_text) ? details?.opening_hours?.weekday_text : fields.opening_hours_weekday_text) as any,
                            enrich_place_id: pid || null,
                            enrich_place_name: s.name || details?.name || draft?.enrich_place_name || null,
                            enrich_place_lat: details?.geometry?.lat ?? fields.enrich_place_lat ?? null,
                            enrich_place_lng: details?.geometry?.lng ?? fields.enrich_place_lng ?? null,
                            google_maps_url: details?.url || mapsUrl || fields.google_maps_url,
                          };
                          (next as any).open_now = (typeof details?.opening_hours?.open_now === 'boolean' ? details?.opening_hours?.open_now : (fields as any)?.open_now);
                          setFields(next);
                          onConfirm(next);
                        }}
                      >
                        {useSuggestionLabel}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Autocomplete predictions */}
            {!!suggestions.length && (
              <div className="mt-2 rounded border border-gray-200 dark:border-gray-800">
                {suggestions.map((s: any, i: number) => (
                  <button
                    key={i}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:hover:bg-gray-800"
                    onClick={async () => {
                      const name = s.structured_formatting?.main_text || "";
                      const address = s.description || "";
                      let pid: string | undefined;
                      try {
                        pid = s.place_id;
                      } catch {}
                      let details: any = null;
                      if (pid) {
                        try {
                          details = await getPlaceDetailsServer(pid);
                        } catch {}
                      }
                      const mapsUrl = pid
                        ? `https://www.google.com/maps/place/?q=place_id:${pid}`
                        : undefined;
                      setFields((f) => {
                        const next: Draft = {
                          ...f,
                          restaurant_name: name || details?.name || f.restaurant_name,
                          restaurant_address: address || details?.formatted_address || f.restaurant_address,
                          enrich_place_id: pid || f.enrich_place_id || null,
                          enrich_place_name: name || details?.name || f.enrich_place_name || null,
                          enrich_place_lat: details?.geometry?.lat ?? f.enrich_place_lat ?? null,
                          enrich_place_lng: details?.geometry?.lng ?? f.enrich_place_lng ?? null,
                          restaurant_rating: (details?.rating ?? f.restaurant_rating) as any,
                          opening_hours_weekday_text:
                            (Array.isArray(details?.opening_hours?.weekday_text) ? details?.opening_hours?.weekday_text : f.opening_hours_weekday_text) as any,
                          google_maps_url: details?.url || mapsUrl || f.google_maps_url,
                        };
                        (next as any).open_now = (typeof details?.opening_hours?.open_now === 'boolean' ? details?.opening_hours?.open_now : (f as any)?.open_now);
                        return next;
                      });
                      geocodeAddress(address).then((p) => p && setPos(p)).catch(() => {});
                    }}
                  >
                    <div className="truncate font-medium">
                      {s.structured_formatting?.main_text || s.description}
                    </div>
                    <div className="truncate text-xs text-gray-500">
                      {s.structured_formatting?.secondary_text || ""}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Live results from server search */}
            <div className="mt-2">
              <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
                {isSearching ? "Searching…" : liveResults.length ? "Live results" : null}
              </div>
              {!!liveResults.length && (
                <div className="rounded border border-gray-200 dark:border-gray-800">
                  {liveResults.map((r) => (
                    <button
                      key={r.place_id}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:hover:bg-gray-800"
                      onClick={async () => {
                        const mapsUrl = r.place_id
                          ? `https://www.google.com/maps/place/?q=place_id:${r.place_id}`
                          : undefined;
                        const next: Draft = {
                          ...fields,
                          restaurant_name: r.name || fields.restaurant_name,
                          restaurant_address: r.formatted_address || fields.restaurant_address,
                          enrich_place_id: r.place_id,
                          enrich_place_name: r.name,
                          enrich_place_lat: r.geometry?.lat ?? fields.enrich_place_lat ?? null,
                          enrich_place_lng: r.geometry?.lng ?? fields.enrich_place_lng ?? null,
                          google_maps_url: mapsUrl || fields.google_maps_url,
                        };
                        try {
                          const d: any = await getPlaceDetailsServer(r.place_id);
                          if (d) {
                            next.restaurant_rating = (d.rating ?? next.restaurant_rating) as any;
                            next.opening_hours_weekday_text =
                              (Array.isArray(d.opening_hours?.weekday_text) ? d.opening_hours.weekday_text : next.opening_hours_weekday_text) as any;
                            next.google_maps_url = d.url || next.google_maps_url;
                            (next as any).open_now = (typeof d.opening_hours?.open_now === 'boolean' ? d.opening_hours.open_now : (next as any).open_now);
                            if (d.geometry?.lat && d.geometry?.lng) {
                              next.enrich_place_lat = d.geometry.lat;
                              next.enrich_place_lng = d.geometry.lng;
                            }
                          }
                        } catch {}
                        setFields(next);
                        if (r.geometry?.lat && r.geometry?.lng) setPos({ lat: r.geometry.lat, lng: r.geometry.lng });
                      }}
                    >
                      <div className="truncate font-medium">{r.name}</div>
                      <div className="truncate text-xs text-gray-500">{r.formatted_address}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: map */}
          <div className="min-h-[220px]">
            {ready ? (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: 220, borderRadius: 12 }}
                center={pos || { lat: 0, lng: 0 }}
                zoom={pos ? 14 : 2}
                options={{ disableDefaultUI: true }}
              >
                {pos && <Marker position={pos} />}
              </GoogleMap>
            ) : (
              <div className="grid h-[220px] place-items-center text-sm text-gray-500">Loading map…</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <a
            href="https://support.foodspotter.example/why-cant-we-parse"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-600 hover:underline"
          >
            Why can’t we parse this?
          </a>
          <div className="ml-auto flex items-center gap-2">
            {useSuggested && (
              <button
                type="button"
                className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                onClick={() => {
                  const next: Draft = {
                    ...fields,
                    restaurant_name: draft?.enrich_place_name || fields.restaurant_name,
                    restaurant_address: draft?.enrich_place_address || fields.restaurant_address,
                  };
                  onConfirm(next);
                }}
              >
                Use suggested place
              </button>
            )}
            <button
              type="button"
              onClick={() => onConfirm(fields)}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(body, document.body) : body;
}
